import { cn } from '@plato/ui/util/classes';
import { useEffect, useMemo, useState, type ReactElement, type ReactNode } from 'react';
import { TabsHeader } from './tabs-header.tsx';

type TabsLegacyItemProps = {
    label: string;
    tabId: string;
    children?: ReactNode;
    className?: string;
};

export function TabsLegacyItem({ children, className = '' }: TabsLegacyItemProps) {
    return <div className={cn('flex', className)}>{children}</div>;
}

type TabsLegacyProps = {
    children: ReactNode;
    className?: string;
    defaultTab?: string;
    onTabChange?: (tabId: string) => void;
};

export function TabsLegacy({ children, className, defaultTab, onTabChange }: TabsLegacyProps) {
    const [selectedTabKey, setSelectedTabKey] = useState<string | null>(defaultTab || null);

    const cleanChildren = useMemo<ReactElement<TabsLegacyItemProps>[]>(() => {
        return (Array.isArray(children) ? children : [children]).filter(
            (c): c is ReactElement<TabsLegacyItemProps> => Boolean(c),
        );
    }, [children]);

    useEffect(() => {
        if (onTabChange && selectedTabKey) {
            onTabChange(selectedTabKey);
        }
    }, [onTabChange, selectedTabKey]);

    const isActiveTab = (index: number, tabId: string) => {
        return (
            (selectedTabKey === null && index === 0) || (selectedTabKey !== null && selectedTabKey === tabId)
        );
    };

    const tabs = cleanChildren.map((child) => child.props.tabId);

    return (
        <div className={cn('flex w-full flex-col gap-3', className)}>
            <TabsHeader
                tabs={tabs}
                getTabLabel={(tab) => cleanChildren[tabs.indexOf(tab)]?.props.label ?? tab}
                selectedTabKey={selectedTabKey ?? tabs[0] ?? ''}
                onTabChange={setSelectedTabKey}
            />

            {cleanChildren.map((child, index) => {
                if (isActiveTab(index, child.props.tabId)) {
                    return child;
                } else {
                    return null;
                }
            })}
        </div>
    );
}
